import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";

// ---- Countries ----
const COUNTRIES = [
  { code: "US", name: "United States", region: "americas" },
  { code: "GB", name: "United Kingdom", region: "eu" },
  { code: "DE", name: "Germany", region: "eu" },
  { code: "FR", name: "France", region: "eu" },
  { code: "NL", name: "Netherlands", region: "eu" },
  { code: "IT", name: "Italy", region: "eu" },
  { code: "ES", name: "Spain", region: "eu" },
  { code: "BE", name: "Belgium", region: "eu" },
  { code: "AT", name: "Austria", region: "eu" },
  { code: "SE", name: "Sweden", region: "eu" },
  { code: "CH", name: "Switzerland", region: "eu" },
  { code: "AE", name: "United Arab Emirates", region: "gcc" },
  { code: "SA", name: "Saudi Arabia", region: "gcc" },
  { code: "QA", name: "Qatar", region: "gcc" },
  { code: "KW", name: "Kuwait", region: "gcc" },
  { code: "BH", name: "Bahrain", region: "gcc" },
  { code: "OM", name: "Oman", region: "gcc" },
  { code: "SG", name: "Singapore", region: "asia" },
  { code: "JP", name: "Japan", region: "asia" },
  { code: "CN", name: "China", region: "asia" },
  { code: "KR", name: "South Korea", region: "asia" },
  { code: "AU", name: "Australia", region: "asia" },
  { code: "HK", name: "Hong Kong", region: "asia" },
  { code: "TW", name: "Taiwan", region: "asia" },
  { code: "MY", name: "Malaysia", region: "asia" },
  { code: "TH", name: "Thailand", region: "asia" },
  { code: "CA", name: "Canada", region: "americas" },
  { code: "MX", name: "Mexico", region: "americas" },
  { code: "BR", name: "Brazil", region: "americas" },
  { code: "ZA", name: "South Africa", region: "africa" },
  { code: "KE", name: "Kenya", region: "africa" },
  { code: "NG", name: "Nigeria", region: "africa" },
].sort((a, b) => a.name.localeCompare(b.name));

const EU_CODES = ["GB", "DE", "FR", "NL", "IT", "ES", "BE", "AT", "SE", "CH"];
const GCC_CODES = ["AE", "SA", "QA", "KW", "BH", "OM"];

const CURRENCIES = [
  { code: "USD", label: "US Dollar" }, { code: "EUR", label: "Euro" }, { code: "GBP", label: "British Pound" },
  { code: "JPY", label: "Japanese Yen" }, { code: "AED", label: "UAE Dirham" }, { code: "SAR", label: "Saudi Riyal" },
  { code: "SGD", label: "Singapore Dollar" }, { code: "CNY", label: "Chinese Yuan" }, { code: "AUD", label: "Australian Dollar" },
  { code: "CAD", label: "Canadian Dollar" }, { code: "CHF", label: "Swiss Franc" }, { code: "HKD", label: "Hong Kong Dollar" },
  { code: "KRW", label: "South Korean Won" }, { code: "MYR", label: "Malaysian Ringgit" }, { code: "THB", label: "Thai Baht" },
  { code: "ZAR", label: "South African Rand" }, { code: "BRL", label: "Brazilian Real" },
];

const INCOTERMS = ["EXW", "FOB", "CIF", "DDP", "DAP", "FCA", "CPT", "CIP", "DAT"];
const INTL_CATEGORIES = ["Manufacturer", "Service Provider", "Agent", "Distributor"];
const PAYMENT_TERMS = [
  { code: "Z001", label: "30 days" }, { code: "Z002", label: "45 days" },
  { code: "Z003", label: "60 days" }, { code: "Z004", label: "90 days" },
  { code: "Z005", label: "Immediate" },
];

const SWIFT_REGEX = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

const INTL_DOCUMENT_TYPES = [
  { id: "doc_bank_verification", label: "Bank Verification Document", desc: "Bank letter on letterhead or SWIFT confirmation", accept: ".pdf", maxMB: 5, required: true },
  { id: "doc_vat_cert",          label: "VAT / Tax Registration Certificate", desc: "Country-specific tax registration", accept: ".pdf", maxMB: 5, hasExpiry: true },
  { id: "doc_tax_id_cert",       label: "Tax ID Certificate", desc: "EIN letter, TRN certificate, or equivalent", accept: ".pdf", maxMB: 5 },
  { id: "doc_incorporation_intl", label: "Certificate of Incorporation", desc: "Company registration in country of origin", accept: ".pdf", maxMB: 5 },
  { id: "doc_trade_licence_intl", label: "Trade Licence", desc: "If applicable in vendor\u2019s jurisdiction", accept: ".pdf", maxMB: 5, hasExpiry: true },
];

/**
 * T2TVendorFormIntl \u2014 International vendor form with 3 sections + documents.
 *
 * Props: same interface as T2TVendorForm (data, onChange, stage, accountGroup, errors, readOnly)
 */
const T2TVendorFormIntl = ({ data = {}, onChange, stage = "initiator", errors = {}, readOnly = false }) => {
  const [activeSection, setActiveSection] = useState(1);

  const isFinanceStage = stage === "finance";
  const isCheckerOrApprover = stage === "checker" || stage === "approver";

  const set = useCallback((field, value) => {
    if (readOnly || isCheckerOrApprover) return;
    if (onChange) onChange(field, value);
  }, [onChange, readOnly, isCheckerOrApprover]);

  const country = data.country_of_origin || "";
  const isEU = EU_CODES.includes(country);
  const isUS = country === "US";
  const isGCC = GCC_CODES.includes(country);

  const fieldErr = (field) => errors[field] ? <div style={{ fontSize: 10, color: "#DC2626", marginTop: 2 }}>{errors[field]}</div> : null;

  const sections = [
    { id: 1, label: "General Info", icon: "\uD83C\uDF10" },
    { id: 2, label: "Tax & Compliance", icon: "\uD83C\uDFDB", financeOnly: true },
    { id: 3, label: "Banking (SWIFT/IBAN)", icon: "\uD83C\uDFE6", financeOnly: true },
    { id: 4, label: "Documents", icon: "\uD83D\uDCCE" },
  ];

  const visibleSections = sections.filter((s) => {
    if (s.financeOnly && stage === "initiator") return false;
    return true;
  });

  return (
    <div className="t2t-vendor-form">
      {/* Section tabs */}
      <div style={{ display: "flex", gap: 2, marginBottom: 20, flexWrap: "wrap" }}>
        {visibleSections.map((s) => (
          <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
            style={{
              padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: "1px solid", transition: "0.15s",
              background: activeSection === s.id ? "var(--t2t-primary)" : "white",
              color: activeSection === s.id ? "white" : "var(--t2t-text-secondary)",
              borderColor: activeSection === s.id ? "var(--t2t-primary)" : "#E2E8F0",
            }}
          >{s.icon} {s.label}</button>
        ))}
      </div>

      {/* ======== SECTION 1: General & Country ======== */}
      {activeSection === 1 && (
        <div className="t2t-vendor-section">
          <div className="t2t-fg">
            <label>Account Group *</label>
            <select className="t2t-fc" value={data.account_group || ""} onChange={(e) => set("account_group", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
              <option value="">Select account group...</option>
              <option value="Z201">Z201 \u2014 International \u2014 Regular</option>
              <option value="Z202">Z202 \u2014 International \u2014 Simplified</option>
            </select>
            {fieldErr("account_group")}
          </div>

          <div className="t2t-fg">
            <label>Company Legal Name * <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(as registered in country of incorporation)</span></label>
            <input className="t2t-fc" value={data.vendor_name || ""} onChange={(e) => set("vendor_name", e.target.value.slice(0, 35))} placeholder="Legal name" disabled={readOnly || isCheckerOrApprover} maxLength={35} />
            {fieldErr("vendor_name")}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>Country of Origin *</label>
              <select className="t2t-fc" value={data.country_of_origin || ""} onChange={(e) => set("country_of_origin", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
                <option value="">Select country...</option>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
              </select>
              {fieldErr("country_of_origin")}
            </div>
            <div className="t2t-fg">
              <label>Transaction Currency *</label>
              <select className="t2t-fc" value={data.currency || ""} onChange={(e) => set("currency", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
                <option value="">Select currency...</option>
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} \u2014 {c.label}</option>)}
              </select>
              {fieldErr("currency")}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>Vendor Category *</label>
              <select className="t2t-fc" value={data.vendor_category || ""} onChange={(e) => set("vendor_category", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
                <option value="">Select...</option>
                {INTL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {fieldErr("vendor_category")}
            </div>
            <div className="t2t-fg">
              <label>Incoterms</label>
              <select className="t2t-fc" value={data.incoterms || ""} onChange={(e) => set("incoterms", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
                <option value="">Select (for goods imports)...</option>
                {INCOTERMS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>

          <div className="t2t-fg">
            <label>Payment Terms *</label>
            <select className="t2t-fc" value={data.payment_terms || ""} onChange={(e) => set("payment_terms", e.target.value)} disabled={readOnly || isCheckerOrApprover}>
              <option value="">Select payment terms...</option>
              {PAYMENT_TERMS.map((t) => <option key={t.code} value={t.code}>{t.code} \u2014 {t.label}</option>)}
            </select>
            {fieldErr("payment_terms")}
          </div>

          {/* Address */}
          <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 14, marginTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Registered Address</div>
            <div className="t2t-fg">
              <label>Street Address *</label>
              <input className="t2t-fc" value={data.street_address || ""} onChange={(e) => set("street_address", e.target.value)} disabled={readOnly || isCheckerOrApprover} />
              {fieldErr("street_address")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div className="t2t-fg">
                <label>City *</label>
                <input className="t2t-fc" value={data.city || ""} onChange={(e) => set("city", e.target.value)} disabled={readOnly || isCheckerOrApprover} />
              </div>
              <div className="t2t-fg">
                <label>State / Region</label>
                <input className="t2t-fc" value={data.state_code || ""} onChange={(e) => set("state_code", e.target.value)} disabled={readOnly || isCheckerOrApprover} />
              </div>
              <div className="t2t-fg">
                <label>Postal Code *</label>
                <input className="t2t-fc" value={data.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} disabled={readOnly || isCheckerOrApprover} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div className="t2t-fg">
                <label>Phone</label>
                <input className="t2t-fc" value={data.phone_number || ""} onChange={(e) => set("phone_number", e.target.value)} placeholder="With country code" disabled={readOnly || isCheckerOrApprover} />
              </div>
              <div className="t2t-fg">
                <label>Email</label>
                <input className="t2t-fc" type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} disabled={readOnly || isCheckerOrApprover} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======== SECTION 2: Tax & Compliance (Finance Stage) ======== */}
      {activeSection === 2 && (
        <div className="t2t-vendor-section">
          {!isFinanceStage && !isCheckerOrApprover && (
            <div style={{ padding: 12, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 6, fontSize: 12, color: "#9A3412", marginBottom: 16 }}>
              Tax fields are entered by the Finance Data Controller at the Finance stage.
            </div>
          )}

          {!country && (
            <div style={{ padding: 16, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 13 }}>
              Select a Country of Origin in General Info first \u2014 tax fields are country-specific.
            </div>
          )}

          {country && (
            <>
              {/* Country-specific tax field info */}
              <div style={{ padding: 10, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 6, fontSize: 12, color: "#1E40AF", marginBottom: 16 }}>
                Showing tax fields for: <strong>{COUNTRIES.find((c) => c.code === country)?.name || country}</strong>
                {isEU && " (EU \u2014 VAT required)"}
                {isUS && " (US \u2014 EIN/FEIN required)"}
                {isGCC && " (GCC \u2014 TRN required)"}
              </div>

              {/* EU: VAT */}
              {isEU && (
                <div className="t2t-fg">
                  <label>VAT Registration Number *</label>
                  <input className="t2t-fc" value={data.vat_number || ""} onChange={(e) => set("vat_number", e.target.value.toUpperCase())}
                    placeholder={`e.g. ${country}123456789`} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
                  {fieldErr("vat_number")}
                </div>
              )}

              {/* US: EIN */}
              {isUS && (
                <div className="t2t-fg">
                  <label>EIN / Federal Tax ID * <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(XX-XXXXXXX)</span></label>
                  <input className="t2t-fc" value={data.ein_number || ""} onChange={(e) => set("ein_number", e.target.value)}
                    placeholder="e.g. 12-3456789" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} maxLength={10} />
                  {fieldErr("ein_number")}
                </div>
              )}

              {/* GCC: TRN */}
              {isGCC && (
                <div className="t2t-fg">
                  <label>TRN (Tax Registration Number) *</label>
                  <input className="t2t-fc" value={data.trn_number || ""} onChange={(e) => set("trn_number", e.target.value)}
                    placeholder="VAT/TRN registration number" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
                  {fieldErr("trn_number")}
                </div>
              )}

              {/* Others: Generic Tax ID */}
              {!isEU && !isUS && !isGCC && (
                <div className="t2t-fg">
                  <label>Tax Identification Number *</label>
                  <input className="t2t-fc" value={data.tax_id_generic || ""} onChange={(e) => set("tax_id_generic", e.target.value)}
                    placeholder="Country-specific tax ID" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
                  {fieldErr("tax_id_generic")}
                </div>
              )}

              {/* TDS for international */}
              <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 14, marginTop: 14 }}>
                <div className="t2t-fg">
                  <label>TDS Applicable?</label>
                  <div style={{ display: "flex", gap: 12 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                      <input type="radio" checked={data.withholding_tds === true} onChange={() => set("withholding_tds", true)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} /> Yes
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                      <input type="radio" checked={data.withholding_tds !== true} onChange={() => set("withholding_tds", false)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} /> No
                    </label>
                  </div>
                </div>

                {data.withholding_tds && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="t2t-fg">
                      <label>TDS Section</label>
                      <select className="t2t-fc" value={data.tds_section || "195"} onChange={(e) => set("tds_section", e.target.value)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)}>
                        <option value="195">195 (Non-resident payments)</option>
                        <option value="194C">194C (Contractor)</option>
                        <option value="194J">194J (Professional services)</option>
                      </select>
                    </div>
                    <div className="t2t-fg">
                      <label>TDS Rate %</label>
                      <input className="t2t-fc" type="number" min="0" max="30" value={data.tds_rate || ""} onChange={(e) => set("tds_rate", e.target.value)}
                        placeholder="0-30" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ======== SECTION 3: International Banking (SWIFT/IBAN) ======== */}
      {activeSection === 3 && (
        <div className="t2t-vendor-section">
          {!isFinanceStage && !isCheckerOrApprover && (
            <div style={{ padding: 12, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 6, fontSize: 12, color: "#9A3412", marginBottom: 16 }}>
              Banking details are entered by the Finance Data Controller at the Finance stage.
            </div>
          )}

          <div className="t2t-fg">
            <label>Bank Name *</label>
            <input className="t2t-fc" value={data.intl_bank_name || ""} onChange={(e) => set("intl_bank_name", e.target.value)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
            {fieldErr("intl_bank_name")}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="t2t-fg">
              <label>Bank Country *</label>
              <select className="t2t-fc" value={data.intl_bank_country || ""} onChange={(e) => set("intl_bank_country", e.target.value)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)}>
                <option value="">Select bank country...</option>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
              {fieldErr("intl_bank_country")}
            </div>
            <div className="t2t-fg">
              <label>Account Currency *</label>
              <select className="t2t-fc" value={data.bank_currency || ""} onChange={(e) => set("bank_currency", e.target.value)} disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)}>
                <option value="">Select...</option>
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
              {fieldErr("bank_currency")}
            </div>
          </div>

          <div className="t2t-fg">
            <label>Account Number *</label>
            <input className="t2t-fc" value={data.intl_account_number || ""} onChange={(e) => set("intl_account_number", e.target.value)}
              placeholder="Local account number or IBAN" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} />
            {fieldErr("intl_account_number")}
          </div>

          <div className="t2t-fg">
            <label>IBAN <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(if applicable \u2014 EU/international accounts)</span></label>
            <input className="t2t-fc" value={data.iban || ""} onChange={(e) => set("iban", e.target.value.toUpperCase().replace(/\s/g, ""))}
              placeholder="e.g. DE89370400440532013000" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} maxLength={34} />
          </div>

          <div className="t2t-fg">
            <label>SWIFT / BIC Code * <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(8 or 11 characters)</span></label>
            <input className="t2t-fc" value={data.swift_bic || ""} onChange={(e) => set("swift_bic", e.target.value.toUpperCase().slice(0, 11))}
              placeholder="e.g. DEUTDEFF" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} maxLength={11}
              style={{ borderColor: data.swift_bic && !SWIFT_REGEX.test(data.swift_bic) ? "#DC2626" : data.swift_bic && SWIFT_REGEX.test(data.swift_bic) ? "#16A34A" : undefined }} />
            {data.swift_bic && SWIFT_REGEX.test(data.swift_bic) && <div style={{ fontSize: 10, color: "#16A34A", marginTop: 2 }}>Valid SWIFT format</div>}
            {data.swift_bic && data.swift_bic.length >= 8 && !SWIFT_REGEX.test(data.swift_bic) && <div style={{ fontSize: 10, color: "#DC2626", marginTop: 2 }}>Invalid SWIFT format (8 or 11 uppercase letters/digits)</div>}
            {fieldErr("swift_bic")}
          </div>

          <div className="t2t-fg">
            <label>Intermediary Bank SWIFT <span style={{ fontWeight: 400, fontSize: 10, color: "var(--t2t-text-muted)" }}>(for USD payments via correspondent bank)</span></label>
            <input className="t2t-fc" value={data.intermediary_bank || ""} onChange={(e) => set("intermediary_bank", e.target.value.toUpperCase().slice(0, 11))}
              placeholder="Optional" disabled={readOnly || (!isFinanceStage && isCheckerOrApprover)} maxLength={11} />
          </div>
        </div>
      )}

      {/* ======== SECTION 4: Documents ======== */}
      {activeSection === 4 && (
        <div className="t2t-vendor-section">
          <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 16 }}>
            Upload required international vendor documents. PDF format, max 5MB per file.
          </p>
          {INTL_DOCUMENT_TYPES.map((doc) => (
            <div key={doc.id} style={{ padding: "12px 0", borderBottom: "1px solid #F1F5F9" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>
                    {doc.label} {doc.required && <span style={{ color: "#DC2626" }}>*</span>}
                  </label>
                  <div style={{ fontSize: 10, color: "var(--t2t-text-muted)" }}>{doc.desc}</div>
                </div>
                {data[doc.id] && <span style={{ fontSize: 10, color: "#16A34A", fontWeight: 600 }}>Uploaded</span>}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="file" accept={doc.accept}
                  onChange={(e) => set(doc.id, e.target.files[0] || null)}
                  disabled={readOnly || isCheckerOrApprover}
                  style={{ fontSize: 12 }} />
                {doc.hasExpiry && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <label style={{ fontSize: 11, color: "var(--t2t-text-muted)", whiteSpace: "nowrap" }}>Expiry:</label>
                    <input type="date" className="t2t-fc" style={{ fontSize: 11, width: 140, padding: "3px 6px" }}
                      value={data[`${doc.id}_expiry`] || ""} onChange={(e) => set(`${doc.id}_expiry`, e.target.value)}
                      disabled={readOnly || isCheckerOrApprover} />
                  </div>
                )}
              </div>
              {fieldErr(doc.id)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

T2TVendorFormIntl.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func,
  stage: PropTypes.oneOf(["initiator", "checker", "finance", "approver"]),
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
};

export default T2TVendorFormIntl;
